import posthog from 'posthog-js'
import type { BeforeSendFn, CaptureResult } from 'posthog-js'
import { POSTHOG, STAGE } from '@/config/constants'
import { exceptionBeforeSend } from './exception-filter'
import { installPhTransport } from './ph-transport'
import { syncSessionReplay } from './session-replay'

/** Whether PostHog is configured (has an API key). */
export const isPostHogEnabled = (): boolean => Boolean(POSTHOG.apiKey)

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

// Recurses into plain objects and arrays only. The event's `timestamp` is a Date, and rebuilding
// it as an object literal made it `{}`: PostHog then got `offset: null` on batched events and
// rejected unbatched ones with a 400 ("did not match any variant of untagged enum RawRequest").
function scrubUrlFragments(value: unknown, key = ''): unknown {
  if (typeof value === 'string' && /url|href|referrer/i.test(key)) return value.split('#', 1)[0]
  if (Array.isArray(value)) return value.map((item) => scrubUrlFragments(item, key))
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, child]) => [
        childKey,
        scrubUrlFragments(child, childKey),
      ]),
    )
  }
  return value
}

/** Single privacy and exception boundary for every PostHog event. */
export const privacyBeforeSend: BeforeSendFn = (capture: CaptureResult | null) => {
  const filtered = exceptionBeforeSend(capture)
  return filtered ? (scrubUrlFragments(filtered) as CaptureResult) : null
}

/**
 * Initialize the PostHog client.
 * No-ops if VITE_POSTHOG_KEY is empty (default in dev).
 */
export function initPostHog(): typeof posthog | null {
  if (!isPostHogEnabled()) return null

  // Strips the vestigial `ip=` param that generic ad-block rules key on, even
  // behind the /ph proxy. Only XHR and sendBeacon can be wrapped after
  // posthog-js has loaded, hence api_transport below. See ph-transport.ts.
  installPhTransport(POSTHOG.apiHost)

  posthog.init(POSTHOG.apiKey, {
    api_host: POSTHOG.apiHost,
    api_transport: 'XHR',
    // When api_host is a reverse proxy, this is where toolbar and replay
    // deep links resolve. Harmless while api_host is still PostHog itself.
    ui_host: 'https://us.posthog.com',

    // 'history_change' is the only value that captures SPA route changes;
    // `true` captures once per hard load and never again under
    // createBrowserRouter. The initial pageview still fires under either.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    autocapture: false,

    // Real crashes only. Console errors stay off permanently: every
    // console.error in the app would otherwise become a billed event.
    // What does get captured is bounded further by before_send.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    before_send: privacyBeforeSend,

    // Session replay stays off at load. The replay flags are the only thing
    // that starts it (session-replay.ts, wired below).
    disable_session_recording: true,
    session_recording: {
      maskTextSelector: '[data-ph-mask]',
      blockSelector: '[data-ph-no-capture]',
      maskAllInputs: false,
      recordCrossOriginIframes: false,
    },

    // Privacy
    mask_personal_data_properties: true,

    // Performance
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      // In dev with PostHog enabled, log to console for debugging
      if (import.meta.env.DEV) {
        ph.debug()
      }
    },
  })

  // Two PostHog projects: prod has its own; staging, beta and local share the
  // non-prod one. Stamping the stage on every event is what separates them
  // inside the shared project.
  posthog.register({ environment: currentEnvironment() })
  applyFlagEnvironment()
  posthog.onFeatureFlags(() => syncSessionReplay(posthog))

  return posthog
}

function currentEnvironment(): string {
  return STAGE || 'local'
}

/**
 * Lets flag release conditions target `environment` (e.g. on in staging, off
 * in beta), which they cannot do from the event super property above. Set as
 * a flag-evaluation override rather than a person property so a user active
 * in both staging and beta is not flipped by whichever loaded last.
 * posthog.reset() clears it, so the logout path calls this again.
 */
export function applyFlagEnvironment(): void {
  if (!isPostHogEnabled()) return
  posthog.setPersonPropertiesForFlags({ environment: currentEnvironment() })
}

export { posthog }
